import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { assetUrl } from '@/lib/assetUrl';
import { MAIN_MENU_ITEMS, TOOL_MENU_ITEMS, type MainMenuItem } from '@/lib/menuConfig';
import { prefetchBattlepassStatic } from '@/hooks/useBattlepass';
import { prefetchCalendar } from '@/hooks/useCalendar';
import { prefetchSpinState } from '@/hooks/useSpin';
import { prefetchSpecialOffers } from '@/hooks/useSpecialOffer';
import { prefetchAuction } from '@/hooks/useAuction';
import { prefetchTitleManager } from '@/hooks/useTitleManager';
import { prefetchRanking } from '@/hooks/useRanking';
import { prefetchSchedule } from '@/hooks/useSchedule';
import { prefetchRegister } from '@/hooks/useRegister';
import { prefetchDropLogs } from '@/hooks/useDropLogs';
import { prefetchUniquesQueries } from '@/hooks/useUniquesHistory';
import { prefetchChatbotHistory } from '@/hooks/useChatbot';
import { decodeJwtPayload, getStoredJwt } from '@/lib/axios';
import { useLocale } from '@/context/LocaleContext';
import '@/styles/ingame-topnav.css';

/** Home: grid principal — sin cambios de diseño. */
export function MenuGrid() {
  const { t } = useLocale();

  return (
    <div className="menu-grid">
      {MAIN_MENU_ITEMS.map((item) => (
        <div className="menu-item" key={item.to}>
          <Link to={item.to}>
            <img src={assetUrl(`/assets/icons/menu/${item.icon}`)} alt="" className="menu-icon" />
            <span>{t(item.labelKey)}</span>
          </Link>
        </div>
      ))}
    </div>
  );
}

function useMenuPrefetch() {
  const queryClient = useQueryClient();

  return useCallback(
    (item: MainMenuItem) => {
      switch (item.prefetch) {
        case 'battlepass':
          prefetchBattlepassStatic(queryClient);
          break;
        case 'calendar':
          prefetchCalendar(queryClient);
          break;
        case 'spin':
          prefetchSpinState(queryClient);
          break;
        case 'specialoffert':
          prefetchSpecialOffers(queryClient);
          break;
        case 'auction':
          prefetchAuction(queryClient);
          break;
        case 'titlemanager':
          prefetchTitleManager(queryClient);
          break;
        case 'ranking':
          prefetchRanking(queryClient);
          break;
        case 'uniques':
          prefetchUniquesQueries(queryClient);
          break;
        case 'schedule':
          prefetchSchedule(queryClient);
          break;
        case 'register':
          prefetchRegister(queryClient);
          break;
        case 'droplogs':
          prefetchDropLogs(queryClient);
          break;
        case 'chatbot': {
          const jwt = getStoredJwt();
          const payload = jwt ? decodeJwtPayload(jwt) : null;
          if (payload?.charId) prefetchChatbotHistory(queryClient, payload.charId);
          break;
        }
        default:
          break;
      }
    },
    [queryClient],
  );
}

export interface SimpleMenuProps {
  /**
   * `bar` — hamburger + volver + slot de header/filtros (páginas normales).
   * `minimal` — solo botón de menú (Battle Pass).
   */
  variant?: 'bar' | 'minimal';
  backTo?: string;
  /** Título corto de sección (estilo filtro ODIN). */
  title?: string;
  /** Subtítulo / eyebrow encima del título. */
  subtitle?: string;
  /** Contenido a la derecha del header (filtros, búsqueda, toggles…). */
  children?: ReactNode;
}

/**
 * Navegación de páginas internas: menú desplegable desde arriba.
 * Home no usa este componente.
 */
export function SimpleMenu({ variant = 'bar', title, subtitle, children, backTo }: SimpleMenuProps) {
  const { t } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const burgerRef = useRef<HTMLButtonElement>(null);
  const prefetch = useMenuPrefetch();

  const close = useCallback(() => {
    setOpen(false);
    burgerRef.current?.blur();
  }, []);
  const toggle = useCallback(() => {
    setOpen((v) => {
      if (v) burgerRef.current?.blur();
      return !v;
    });
  }, []);

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  const goHome = () => {
    close();
    navigate('/');
  };

  return (
    <>
      <div
        className={`ingame-topnav${variant === 'minimal' ? ' ingame-topnav--minimal' : ''}${open ? ' is-menu-open' : ''}`}
      >
        <button
          ref={burgerRef}
          type="button"
          className={`ingame-topnav-burger${open ? ' is-open' : ''}`}
          aria-label={open ? t('menu_close') : t('menu_open')}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={toggle}
        >
          {open ? (
            <i className="ph-light ph-x ingame-topnav-burger-x" aria-hidden />
          ) : (
            <span className="ingame-topnav-burger-lines" aria-hidden>
              <span />
              <span />
              <span />
            </span>
          )}
        </button>

        {variant === 'bar' && !open && (
          <>
            <button type="button" className="ingame-topnav-home" aria-label={t(backTo ? 'ranking_profile_back' : 'menu_home')} onClick={backTo ? () => navigate(backTo) : goHome}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {(title || subtitle) && (
              <div className="ingame-topnav-titles">
                {subtitle ? <div className="ingame-topnav-subtitle">{subtitle}</div> : null}
                {title ? <div className="ingame-topnav-title">{title}</div> : null}
              </div>
            )}

            {children ? <div className="ingame-topnav-actions">{children}</div> : null}
          </>
        )}
      </div>

      {open && (
        <div
          id={panelId}
          className="ingame-menu-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('menu_open')}
        >
          <div className="ingame-menu-panel">
            <div className="ingame-menu-panel-head">
              <img
                src={assetUrl('/assets/images/logos/white-republic-logo.png')}
                alt="Republic"
                className="ingame-menu-logo"
              />
            </div>

            <div className="ingame-menu-body">
              <div className="ingame-menu-section">
                <div className="ingame-menu-section-label">{t('menu_main')}</div>
                <div className="ingame-menu-grid ingame-menu-grid--main">
                  {MAIN_MENU_ITEMS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`ingame-menu-tile${isActive(item.to) ? ' is-active' : ''}`}
                      onMouseEnter={() => prefetch(item)}
                      onClick={close}
                    >
                      <img
                        src={assetUrl(`/assets/icons/menu/${item.icon}`)}
                        alt=""
                        className="ingame-menu-tile-icon"
                      />
                      <span className="ingame-menu-tile-label">{t(item.labelKey)}</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="ingame-menu-section">
                <div className="ingame-menu-section-label">{t('menu_tools')}</div>
                <div className="ingame-menu-grid ingame-menu-grid--tools">
                  {TOOL_MENU_ITEMS.map((item) =>
                    item.external ? (
                      <a key={item.to} href={item.to} className="ingame-menu-tile" onClick={close}>
                        <i className={`${item.phIcon} ingame-menu-tile-ph`} aria-hidden />
                        <span className="ingame-menu-tile-label">{t(item.labelKey)}</span>
                      </a>
                    ) : (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`ingame-menu-tile${isActive(item.to) ? ' is-active' : ''}`}
                        onClick={close}
                      >
                        <i className={`${item.phIcon} ingame-menu-tile-ph`} aria-hidden />
                        <span className="ingame-menu-tile-label">{t(item.labelKey)}</span>
                      </Link>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
